'use client';

import React, { useState, useEffect } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Ad {
  id: string;
  title: string;
  image_url: string | null;
  target_url: string | null;
}

const AD_SHOWN_KEY = 'ww_ad_shown_at';
const SHOW_AFTER_MS = 30_000; // 30 seconds
const COOLDOWN_MS = 20 * 60 * 1000; // 20 minutes cooldown before showing again

export default function AdPopup() {
  const [ad, setAd] = useState<Ad | null>(null);
  const [visible, setVisible] = useState(false);
  const supabase = createClient();

  useEffect(() => {
    // Check cooldown: don't show if shown within last 4 hours
    const lastShown = localStorage.getItem(AD_SHOWN_KEY);
    if (lastShown) {
      const diff = Date.now() - parseInt(lastShown, 10);
      if (diff < COOLDOWN_MS) return;
    }

    // Fetch a random active popup ad
    const fetchAd = async () => {
      const { data } = await supabase
        .from('ads')
        .select('id, title, image_url, target_url')
        .eq('is_active', true)
        .eq('placement', 'popup')
        .limit(10);

      if (!data || data.length === 0) return;
      // Pick a random one
      const picked = data[Math.floor(Math.random() * data.length)];
      setAd(picked);
    };

    fetchAd();
  }, []);

  useEffect(() => {
    if (!ad) return;
    const timer = setTimeout(() => {
      setVisible(true);
      localStorage.setItem(AD_SHOWN_KEY, String(Date.now()));
    }, SHOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [ad]);

  const handleClose = () => setVisible(false);

  const handleClick = () => {
    if (ad?.target_url) {
      window.open(ad.target_url, '_blank', 'noopener,noreferrer');
    }
    handleClose();
  };

  if (!visible || !ad) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={handleClose}
    >
      <div
        className="w-full max-w-mobile mx-3 rounded-t-2xl overflow-hidden"
        style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderBottom: 'none',
          maxHeight: '80vh',
          animation: 'slideUp 0.35s cubic-bezier(0.34,1.56,0.64,1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>
            Werbung
          </span>
          <button
            onClick={handleClose}
            className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
            aria-label="Schließen"
          >
            <X size={14} color="var(--card-foreground)" />
          </button>
        </div>

        {/* Ad image */}
        {ad.image_url && (
          <div className="mx-4 rounded-app overflow-hidden" style={{ height: '220px' }}>
            <img
              src={ad.image_url}
              alt={ad.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Ad content */}
        <div className="px-4 py-4">
          <h3 className="text-base font-bold mb-3" style={{ color: 'var(--card-foreground)' }}>
            {ad.title}
          </h3>

          {ad.target_url && (
            <button
              onClick={handleClick}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-app text-sm font-semibold text-white transition-all duration-150 active:scale-95"
              style={{ background: 'var(--primary)' }}
            >
              <ExternalLink size={15} />
              Mehr erfahren
            </button>
          )}

          <button
            onClick={handleClose}
            className="w-full mt-2 py-2.5 rounded-app text-sm font-medium transition-all duration-150 active:scale-95"
            style={{ color: 'var(--muted-foreground)' }}
          >
            Schließen
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
